// PRICE-FIRST (docs/product/cost-audit-2026-10-30.md section 6): every paid tap shows its own price before anything is
// charged, and sends that price (expectCredits) so the server refuses a dearer one (400 CONFIRM_REQUIRED).
// The prices come from GET /api/projects/:id/prices (api/src/v4/pricefirst.ts). The rule for a tap (V4-SP):
// under the user's auto-OK amount, a tap with the price on the button is the yes; over it (and any video), a confirm
// sheet asks first. Pure; unit-tested in price.test.ts.
import type { JobKind } from "./types";

export type PriceStep = "frame" | "clip" | "sfx" | "music" | "script" | "suno";
export type StepPrice = {
  step: PriceStep;
  /** null: it can't be made right now (`why` says what's missing) */
  credits: number | null;
  /** video clips in it (video always needs a yes) */
  clips: number;
  /** over the user's auto-OK amount, or video: ask in a sheet first */
  needsYes: boolean;
  model?: string;
  durationS?: number;
  why?: string;
  /** lip sync (2026-10-30), a talking scene's clip: how it's synced, what that adds, a warning, better models */
  lipSync?: LipPrice;
};
export type LipPrice = { mode: string; on: boolean; takes: number; pass: number; warn: string | null; suggest: { id: string; short: string; audioIn: boolean }[] };
export type Prices = { autoOk: number; prices: Partial<Record<PriceStep, StepPrice>> };

const STEPS: PriceStep[] = ["frame", "clip", "sfx", "music", "script", "suno"];
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const n = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/** The API's answer, checked. A step that's missing or malformed is left out (its button shows no price yet). */
export function normPrices(raw: unknown): Prices {
  const o = isObj(raw) ? raw : {};
  const autoOk = n(o.autoOk) ?? 50;
  const src = isObj(o.prices) ? o.prices : {};
  const prices: Prices["prices"] = {};
  for (const step of STEPS) {
    const p = src[step];
    if (!isObj(p)) continue;
    const credits = n(p.credits);
    const clips = n(p.clips) ?? (step === "clip" ? 1 : 0);
    prices[step] = {
      step,
      credits,
      clips,
      needsYes: typeof p.needsYes === "boolean" ? p.needsYes : credits !== null && needsYes(credits, clips, autoOk),
      ...(typeof p.model === "string" ? { model: p.model } : {}),
      ...(n(p.durationS) !== null ? { durationS: n(p.durationS)! } : {}),
      ...(typeof p.why === "string" ? { why: p.why } : {}),
      ...(isObj(p.lipSync) ? { lipSync: normLip(p.lipSync) } : {}),
    };
  }
  return { autoOk, prices };
}

function normLip(o: Record<string, unknown>): LipPrice {
  const sug = Array.isArray(o.suggest) ? o.suggest.filter(isObj) : [];
  return {
    mode: typeof o.mode === "string" ? o.mode : "none",
    on: o.on !== false,
    takes: n(o.takes) ?? 0,
    pass: n(o.pass) ?? 0,
    warn: typeof o.warn === "string" ? o.warn : null,
    suggest: sug.filter((s) => typeof s.id === "string" && typeof s.short === "string").map((s) => ({ id: s.id as string, short: s.short as string, audioIn: s.audioIn === true })),
  };
}

/** core's rule (spending.ts needsYes): any video, and anything over the auto-OK amount. */
export const needsYes = (credits: number, clips: number, autoOk: number) => clips > 0 || credits > autoOk;

/** "Make it · 15" (no price yet: the plain label). */
export const withPrice = (label: string, credits: number | null | undefined) => (credits == null ? label : `${label} · ${Math.round(credits).toLocaleString("en-US")}`);

/**
 * What a tap on a paid button does:
 *   "go"    start it now with the price on the button as the yes (under the auto-OK amount)
 *   "ask"   open the confirm sheet first (video, or over the amount)
 *   "wait"  no price yet: nothing is started without one
 */
export function tapRule(price: StepPrice | null | undefined): "go" | "ask" | "wait" {
  if (!price || price.credits === null) return "wait";
  return price.needsYes ? "ask" : "go";
}

/** A new price from a refusal, with the rule re-applied (a changed price always asks: the user hasn't seen it). */
export function repriced(price: StepPrice, credits: number): StepPrice {
  return { ...price, credits, needsYes: true };
}

/** The step a failed job's "Try again" makes again (null: not by a tap; a render goes through its own confirm). */
export function retryStep(kind: JobKind): PriceStep | null {
  if (kind === "frame" || kind === "clip" || kind === "music") return kind;
  if (kind === "sfx" || kind === "cues") return "sfx";
  return null;
}

/** The step a Redo sheet makes again, from what's being redone. */
export function redoStep(a: { kind: string; sceneId?: string; voiceId?: string }): PriceStep | null {
  if (a.kind === "frame" || a.kind === "clip") return a.kind;
  if (a.kind === "script") return "script";
  if (a.kind === "audio" && !a.voiceId) return a.sceneId ? "sfx" : "music";
  return null;
}

/** "Redo it for 15 credits" / "Try again for 85 credits": the go button of a confirm sheet. */
export const goWords = (verb: string, credits: number | null | undefined) => (credits == null ? verb : `${verb} for ${Math.round(credits).toLocaleString("en-US")} credits`);
